import {
  Card,
  Chart,
  cn,
  getColorFromCssVariable,
  HorizontalBarsChart,
  OverflowIndicator,
  Tabs,
  Tooltip,
} from "ui";
import { ChartSkeleton } from "./Skeletons";
import { useMoneyHashQuery } from "../../../hooks/useMoneyHashQuery";
import { axiosApiInstance } from "../../../pages/api/axios";
import React, { useMemo } from "react";
import { Heading } from "./Heading";
import { ErrorPage } from "../../../components/ErrorPage";
import {
  MethodPoint,
  OrganizationPoint,
  ProviderPoint,
  SuccessAndAcceptanceMetrics,
} from "../types";
import { twoFixedDigit } from "../../../utils/twoFixedDigit";
import { useUserContext } from "../../../contexts/UserProvider";
import { format, isSameDay } from "date-fns";
import { Filters } from "../../../components/newFilters";
import { useRouter } from "next/router";
import useUpdateEffect from "../../../hooks/useUpdateEffect";
import { InformationCircleIcon } from "icons/outline";
import { useStickyBorder } from "./useStickyBorder";
import { useDefaultDashboardFilters } from "../hooks/useDefaultDashboardFilters";
import { useFeatureFlags } from "../../../hooks/query";
import { useAnalytics } from "../../analytics/analyticsProvider";
import { AlertsBanner } from "../../alerts/components/AlertsBanner";

export const SuccessAndAcceptanceRates = React.forwardRef<
  HTMLElement,
  {
    activeTab?: string;
    onTabChange?: (tab: string) => void;
    sectionTitle?: string;
  }
>(
  (
    {
      activeTab = "overview",
      onTabChange,
      sectionTitle = "Overview of your payments",
    },
    ref
  ) => {
    const router = useRouter();
    const {
      start_date,
      end_date,
      provider,
      account,
      initiation,
      organization,
      provider_live_status,
    } = router.query;
    const { isMoneyHashUser, organizationId } = useUserContext();
    const { featureFlags } = useFeatureFlags();

    const {
      data: successAndAcceptanceMetrics,
      isLoading,
      isError,
      error,
      listMeta,
    } = useMoneyHashQuery<SuccessAndAcceptanceMetrics>({
      queryKey: [
        "success-and-acceptance-rates",
        {
          start_date,
          end_date,
          organization,
          organizationId,
          provider,
          account,
          initiation,
          provider_live_status,
        },
      ],
      queryFn: ({ signal }) =>
        getSuccessAndAcceptanceRates({
          startDate: start_date as string,
          endDate: end_date as string,
          selectedOrg: (organization || organizationId) as string,
          selectedProvider: provider as string,
          selectedAccountsIds: !!account ? (account as string[]) : undefined,
          selectedInitiation: initiation as string,
          selectedProviderLiveStatus: provider_live_status as string,
          signal,
        }),
      enabled: !isMoneyHashUser || (isMoneyHashUser && !!organizationId),
      keepPreviousData: true,
    });
    const { analytics } = useAnalytics();

    const showSkeleton = isLoading || isError || !successAndAcceptanceMetrics;
    const skeletonState = isLoading ? "loading" : "error";

    const isSameDayRange = useMemo(
      () =>
        isSameDay(
          start_date ? new Date(start_date as string) : new Date(),
          end_date ? new Date(end_date as string) : new Date()
        ),
      [start_date, end_date]
    );

    const { sentinelRef, isStuck } = useStickyBorder();

    useDefaultDashboardFilters();

    // account array query param reference changed with other filters change
    const accountMemoizedReference = useMemo(
      () => (typeof account === "string" ? [account] : account || []).join("~"),
      [account]
    );
    useUpdateEffect(() => {
      if (!accountMemoizedReference) return;
      analytics.track("insights_set_account", {
        account_ids: accountMemoizedReference.split("~"),
      });
    }, [accountMemoizedReference]);

    useUpdateEffect(() => {
      if (!start_date || !end_date) return;
      analytics.track("insights_set_duration", {
        start_date,
        end_date,
      });
    }, [start_date, end_date]);

    return (
      <>
        <div ref={sentinelRef} />

        <div
          className={cn(
            "sticky top-0 z-10 mb-2 mt-1 border-b border-transparent bg-foundations-default",
            isStuck && "border-foundations-subtle"
          )}
        >
          {featureFlags.datadog_dashboard_analytics && (
            <Tabs value={activeTab} onValueChange={onTabChange}>
              <Tabs.List>
                <Tabs.Tab value="overview">Overview</Tabs.Tab>
                <Tabs.Tab value="revenue">Revenue</Tabs.Tab>
                <Tabs.Tab value="declines">Declines</Tabs.Tab>
                {featureFlags.new_dashboard_rates_from_marts && (
                  <Tabs.Tab value="recovery">Recovery</Tabs.Tab>
                )}
                <Tabs.Tab value="brands">Brands</Tabs.Tab>
              </Tabs.List>
            </Tabs>
          )}

          <div className="flex flex-col justify-center py-4">
            <Filters
              leftSlot={
                <h2 className="flex-1 text-lg font-medium text-foundations-boldest">
                  {sectionTitle}
                </h2>
              }
              filters={listMeta?.filter_types || []}
              recentStoreKey={"dashboard-insights-filters"}
              dateRangeMode="finiteIntervalDates"
              filtersFor="dashboard"
              nonClearableFilters={["organization", "start_date", "end_date"]}
              dependantFilter={
                isMoneyHashUser
                  ? {
                      name: "account",
                      filterBy: "organization",
                    }
                  : undefined
              }
            />
          </div>
        </div>

        {featureFlags?.alerts && <AlertsBanner />}

        {isError ? (
          <ErrorPage error={error} />
        ) : (
          <section
            ref={ref}
            id="overview-section"
            className="grid scroll-mt-[180px] grid-cols-1 gap-6 md:grid-cols-2"
          >
            {showSkeleton ? (
              <>
                <ChartSkeleton state={skeletonState} className="h-96" />
                <ChartSkeleton state={skeletonState} className="h-96" />
              </>
            ) : (
              <>
                <RateMetrics
                  type="success"
                  isSameDayRange={isSameDayRange}
                  data={{
                    global:
                      successAndAcceptanceMetrics?.rate_metrics
                        ?.organization_success_rates,
                    providers:
                      successAndAcceptanceMetrics?.rate_metrics
                        ?.provider_success_rates,
                    methods:
                      successAndAcceptanceMetrics?.rate_metrics
                        .method_success_rates,
                  }}
                />
                <RateMetrics
                  type="acceptance"
                  isSameDayRange={isSameDayRange}
                  data={{
                    global:
                      successAndAcceptanceMetrics?.rate_metrics
                        ?.organization_acceptance_rates,
                    providers:
                      successAndAcceptanceMetrics?.rate_metrics
                        ?.provider_acceptance_rates,
                    methods:
                      successAndAcceptanceMetrics?.rate_metrics
                        .method_acceptance_rates,
                  }}
                />
              </>
            )}
          </section>
        )}
      </>
    );
  }
);

SuccessAndAcceptanceRates.displayName = "SuccessAndAcceptanceRates";

const RateMetrics = ({
  type,
  data,
  isSameDayRange,
}: {
  type: "success" | "acceptance";
  data: {
    global: Array<OrganizationPoint>;
    providers: Array<ProviderPoint>;
    methods: Array<MethodPoint>;
  };
  isSameDayRange?: boolean;
}) => {
  const color = useMemo(() => {
    const successColor = getColorFromCssVariable(
      "--chart-categorical-2-subtler"
    );
    const acceptanceColor = getColorFromCssVariable(
      "--chart-categorical-1-subtle"
    );
    return type === "success" ? successColor : acceptanceColor;
  }, [type]);

  return (
    <Card disableHover className="flex flex-col p-0">
      <div className="flex items-center gap-1 px-6 py-4">
        <Heading>
          <span className="capitalize">{type}</span> rate
        </Heading>
        <Tooltip>
          <Tooltip.Trigger
            delay={150}
            className="block items-center justify-center text-foundations-subtle"
          >
            <InformationCircleIcon className="size-4" />
          </Tooltip.Trigger>
          <Tooltip.Popup variant="light">
            {type === "success"
              ? "Percentage of payments completed successfully out of all attempts."
              : "Percentage of payment attempts approved by the bank or processor."}
          </Tooltip.Popup>
        </Tooltip>
      </div>

      <Tabs defaultValue="global">
        <Tabs.List className="px-6">
          <Tabs.Tab value="global" className="flex-1 justify-center">
            Global
          </Tabs.Tab>
          <Tabs.Tab value="providers" className="flex-1 justify-center">
            Providers
          </Tabs.Tab>
          <Tabs.Tab value="methods" className="flex-1 justify-center">
            Methods
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="global">
          <GlobalRateChart
            data={data.global}
            color={color}
            isSameDayRange={isSameDayRange}
          />
        </Tabs.Panel>
        <Tabs.Panel value="providers">
          <ProviderRateChart type={type} data={data.providers} color={color} />
        </Tabs.Panel>
        <Tabs.Panel value="methods">
          <MethodRateChart type={type} data={data.methods} color={color} />
        </Tabs.Panel>
      </Tabs>
    </Card>
  );
};

const GlobalRateChart = ({
  data,
  color,
  isSameDayRange,
}: {
  data: Array<OrganizationPoint>;
  color: string;
  isSameDayRange?: boolean;
}) => {
  const [labels, values] = useMemo(
    () => [data.map((d) => d.time), data.map((d) => d.rate)],
    [data]
  );

  return (
    <div className="flex h-80 flex-col pb-6 pl-4">
      <Chart
        key={labels.join("")}
        type="line"
        labels={labels}
        datasets={[
          {
            data: values,
            backgroundColor: color,
            borderColor: color,
          },
        ]}
        className="flex-1"
      >
        <Chart.Canvas
          maxYValue={100}
          formatYLabel={(value) => `${value}%`}
          formatXLabel={(value) =>
            format(new Date(value), isSameDayRange ? "h a" : "MMM d")
          }
        />
        <Chart.Tooltip
          labelFormatter={(value) =>
            `${format(new Date(value), isSameDayRange ? "h a" : "MMM d")}`
          }
          valueFormatter={(value) => `${value}%`}
        />
      </Chart>
    </div>
  );
};

const ProviderRateChart = ({
  data,
  color,
  type,
}: {
  data: Array<ProviderPoint>;
  color: string;
  type: "success" | "acceptance";
}) => {
  const { analytics } = useAnalytics();
  return (
    <div className="relative overflow-hidden rounded-lg">
      <div className="flex h-80 flex-col overflow-y-scroll px-6 pb-10">
        <HorizontalBarsChart
          items={data.map((provider) => ({
            label: provider.provider,
            value: provider.rate,
            color,
          }))}
          formatValue={(item) => `${twoFixedDigit(item.value)}%`}
          onBarHover={(showTooltip, item) => {
            if (showTooltip) {
              analytics.track("insights_provider_details", {
                type,
                provider: item.label,
                percentage: `${twoFixedDigit(item.value)}%`,
              });
            }
          }}
        />
      </div>
      <OverflowIndicator />
    </div>
  );
};

const MethodRateChart = ({
  type,
  data,
  color,
}: {
  data: Array<MethodPoint>;
  color: string;
  type: "success" | "acceptance";
}) => {
  const { analytics } = useAnalytics();
  return (
    <div className="relative overflow-hidden rounded-lg">
      <div className="flex h-80 flex-col overflow-y-scroll px-6 pb-10">
        <HorizontalBarsChart
          items={data.map((method) => ({
            label: method.method,
            value: method.rate,
            color,
          }))}
          formatValue={(item) => `${twoFixedDigit(item.value)}%`}
          onBarHover={(showTooltip, item) => {
            if (showTooltip) {
              analytics.track("insights_method_details", {
                type,
                method: item.label,
                percentage: `${twoFixedDigit(item.value)}%`,
              });
            }
          }}
        />
      </div>
      <OverflowIndicator />
    </div>
  );
};

const getSuccessAndAcceptanceRates = async ({
  startDate,
  endDate,
  selectedOrg,
  selectedProvider,
  selectedAccountsIds,
  selectedInitiation,
  selectedProviderLiveStatus,
  signal,
}: {
  startDate: string;
  endDate: string;
  selectedOrg: string | undefined;
  selectedProvider: string | undefined;
  selectedAccountsIds: string[] | undefined;
  selectedInitiation: string | undefined;
  selectedProviderLiveStatus: string | undefined;
  signal?: AbortSignal;
}) => {
  const today = new Date().toISOString().split("T")[0];

  return axiosApiInstance
    .get("/d/metrics/success_acceptance_rates/", {
      signal,
      params: {
        start_date: startDate || today,
        end_date: endDate || today,
        organization: selectedOrg,
        provider: selectedProvider,
        account: selectedAccountsIds,
        initiation: selectedInitiation,
        provider_live_status: selectedProviderLiveStatus,
      },
    })
    .then((res) => res.data);
};
